import { ref, computed } from 'vue';

/**
 * DonutChart / Progress (v3 homepage) — core logic, shared by both portals.
 *
 * Static segments OR api (segmentMapping + apiFilters), a doc-type chip switcher
 * (when the docType include-filter has >1 values), `{{docType}}` interpolation in
 * heading/centre/link text, and the SVG arc geometry. `self` pattern; caller
 * injects an axios-like `http` and a `getDefinition` getter. Navigation stays in
 * the presentation layer.
 *
 * @param {Object} options
 * @param {{get:Function,post:Function}} options.http
 * @param {() => Object} options.getDefinition  the widget definition
 * @param {() => void} [options.onReady] called once after the first resolve
 */
const TONE_COLOUR = {
  accent: 'var(--v3-accent)',
  teal: 'var(--v3-accent)',
  green: 'var(--success-fg)',
  violet: 'var(--v3-revise)',
  amber: 'var(--v3-warn)',
  rose: 'var(--v3-attn)',
  neutral: '#9AA4B2',
};
const DONUT_CYCLE = ['neutral', 'accent', 'green'];

export function donutChart(options) {
  const { http, getDefinition = () => ({}), onReady = () => {} } = options || {};
  const def = () => getDefinition() || {};

  const self = {
    segmentsResolved: ref([]),
    activeType: ref(''),
    busy: ref(false),
    loading: ref(false),
  };

  // Render mode — 'donut' (default) or 'bar' (stacked completion bar), matching
  // the prototype V3Progress `mode`. Same resolved segments either way.
  self.mode = computed(() => (def().mode === 'bar' ? 'bar' : 'donut'));
  self.heading = computed(() => def().title || 'Progress');
  self.centreLabel = computed(() => def().centreLabel || '');
  self.linkText = computed(() => def().linkText || '');
  self.docTypeFilter = computed(() =>
    (def().apiFilters || []).find(
      (f) => f.field === 'docType' && (f.condition || 'include') === 'include' && Array.isArray(f.values) && f.values.length > 0,
    ),
  );
  self.docTypes = computed(() => (self.docTypeFilter.value ? self.docTypeFilter.value.values : []));
  self.showSwitcher = computed(() => def().dataSource === 'api' && self.docTypes.value.length > 1);
  // The doc type in context — selected chip, else the sole configured value.
  self.activeDocType = computed(() => {
    if (self.showSwitcher.value) return self.activeType.value;
    const f = self.docTypeFilter.value;
    return f && f.values && f.values.length === 1 ? String(f.values[0]) : '';
  });

  self.interpolate = (text) => {
    if (!text) return text;
    return text
      .replace(/\{\{\s*doc[_ ]?type\s*\}\}/gi, self.activeDocType.value)
      .replace(/\s{2,}/g, ' ')
      .trim();
  };
  self.headingDisplay = computed(() => self.interpolate(self.heading.value));
  self.centreLabelDisplay = computed(() => self.interpolate(self.centreLabel.value));
  self.linkTextDisplay = computed(() => self.interpolate(self.linkText.value));

  self.circ = computed(() => 2 * Math.PI * 52);
  self.total = computed(() => self.segmentsResolved.value.reduce((s, x) => s + x.value, 0));
  self.centreValueDisplay = computed(() => {
    const cv = def().centreValue;
    return cv !== '' && cv != null ? cv : self.total.value;
  });
  self.arcs = computed(() => {
    let off = 0;
    return self.segmentsResolved.value.map((s) => {
      const len = self.total.value ? (s.value / self.total.value) * self.circ.value : 0;
      const a = { colour: s.colour, len, off };
      off += len;
      return a;
    });
  });
  self.pct = (v) => (self.total.value ? Math.round((v / self.total.value) * 100) : 0);

  // "% complete" insight (prototype bar-mode head line). Which segments count
  // as complete is config (`doneLabels`) — the pipeline's terminal statuses.
  self.doneLabels = computed(() => {
    const d = def().doneLabels;
    const arr = Array.isArray(d) ? d : typeof d === 'string' && d ? d.split(',') : [];
    return arr.map((s) => String(s).trim().toLowerCase()).filter(Boolean);
  });
  self.completePct = computed(() => {
    if (!self.doneLabels.value.length || !self.total.value) return null;
    const done = self.segmentsResolved.value
      .filter((s) => self.doneLabels.value.includes(String(s.label).toLowerCase()))
      .reduce((sum, s) => sum + s.value, 0);
    return Math.round((done / self.total.value) * 100);
  });
  // "{pct}% complete · {first count} {first label}" — the prototype insight.
  self.insightText = computed(() => {
    if (self.completePct.value == null) return '';
    const first = self.segmentsResolved.value[0];
    const firstBit = first ? ` · ${first.value} ${String(first.label).toLowerCase()}` : '';
    return `${self.completePct.value}% complete${firstBit}`;
  });

  self.colourFor = (seg, i) =>
    (seg && seg.colour) || TONE_COLOUR[seg && seg.tone] || TONE_COLOUR[DONUT_CYCLE[i % DONUT_CYCLE.length]];

  self.setType = (t) => {
    if (t === self.activeType.value) return;
    self.activeType.value = t;
    self.busy.value = true;
    self.resolve();
  };

  function buildParams() {
    const d = def();
    const out = {};
    (d.apiParams || []).forEach((p) => {
      if (p && p.name) out[p.name] = p.value;
    });
    if (Array.isArray(d.apiFilters) && d.apiFilters.length) {
      out.filters = d.apiFilters
        .filter((f) => f.field && f.values && f.values.length)
        .map((f) => {
          const values =
            self.showSwitcher.value && f.field === 'docType' && (f.condition || 'include') === 'include' && self.activeType.value
              ? [self.activeType.value]
              : f.values;
          return { field: f.field, condition: f.condition || 'include', values };
        });
    }
    return out;
  }

  self.resolveApi = async () => {
    const d = def();
    const m = d.segmentMapping || {};
    const lf = m.labelField || 'status';
    const vf = m.valueField || 'count';
    const cf = m.colourField || 'colour';
    const overrides = d.segmentColours || [];
    const params = buildParams();
    const method = (d.apiMethod || 'POST').toUpperCase();
    self.loading.value = true;
    try {
      const resp = method === 'GET' ? await http.get(d.apiEndpoint, { params }) : await http.post(d.apiEndpoint, params);
      const data = Array.isArray(resp.data)
        ? resp.data
        : resp.data && resp.data.Status === 'OK'
          ? resp.data.Data
          : resp.data && resp.data.Data != null
            ? resp.data.Data
            : [];
      const rows = Array.isArray(data) ? data : data.segments || [];
      self.segmentsResolved.value = rows.map((r, i) => {
        const label = r[lf] || '';
        const override = overrides.find((o) => o.label === label);
        return {
          label,
          value: Number(r[vf]) || 0,
          colour: (override && override.colour) || r[cf] || TONE_COLOUR[DONUT_CYCLE[i % DONUT_CYCLE.length]],
        };
      });
    } catch (e) {
      self.segmentsResolved.value = [];
      console.error('[v3 donut] api load failed', e);
    } finally {
      self.busy.value = false;
      self.loading.value = false;
    }
  };

  self.resolve = async () => {
    const d = def();
    if (d.dataSource === 'api' && d.apiEndpoint) {
      await self.resolveApi();
    } else {
      self.segmentsResolved.value = (d.segments || []).map((s, i) => ({
        label: s.label || '',
        value: Number(s.value) || 0,
        colour: self.colourFor(s, i),
      }));
    }
  };

  // Init: seed the active doc type (honouring `defaultType` when it's one of
  // the configured values — prototype prop), do the first resolve, then signal
  // ready once.
  if (self.showSwitcher.value && !self.activeType.value) {
    const dt = def().defaultType;
    self.activeType.value = dt && self.docTypes.value.includes(dt) ? dt : self.docTypes.value[0];
  }
  Promise.resolve(self.resolve()).finally(() => onReady());

  return self;
}
